import feathersClient from '@/feathers-client'
import { frameworkSettings } from '@/data'

/**
 * Preloads all data needed to render project cards in bulk
 * This reduces N API calls to just a few batch calls
 * @param {Array<Object>} projects - Array of project/compass objects
 * @param {Object} store - Vuex store instance
 * @returns {Promise<Map>} Map of compassId -> chart data
 */
export const preloadProjectsData = async (projects, store) => {
  if (!projects || projects.length === 0) {
    return new Map()
  }

  const compassIds = projects.map(p => p.id)
  console.log(`Preloading data for ${compassIds.length} projects...`)

  try {
    // Batch fetch all compasses-frameworks data at once
    const compassFrameworksResponse = await feathersClient.service('compasses-frameworks').find({
      query: {
        compassId: { $in: compassIds },
        $limit: 1000,
      },
    })
    const compassFrameworks = compassFrameworksResponse.data || compassFrameworksResponse

    // Group by compassId for quick lookup
    const frameworksByCompass = compassFrameworks.reduce((acc, cf) => {
      if (!acc[cf.compassId]) {
        acc[cf.compassId] = []
      }
      acc[cf.compassId].push(cf)
      return acc
    }, {})

    // Get all unique framework IDs that we need
    const frameworkIds = [...new Set(compassFrameworks.map(cf => cf.frameworkId))]

    // Batch fetch all compass-categories for all frameworks and compasses
    const categoriesResponse = await feathersClient.service('compass-categories').find({
      query: {
        compassId: { $in: compassIds },
        frameworkId: { $in: frameworkIds },
        $limit: 10000,
      },
    })
    const allCategories = categoriesResponse.data || categoriesResponse

    // Group categories by compassId and frameworkId
    const categoriesByCompassAndFramework = allCategories.reduce((acc, cat) => {
      const key = `${cat.compassId}_${cat.frameworkId}`
      if (!acc[key]) {
        acc[key] = []
      }
      acc[key].push(cat)
      return acc
    }, {})

    // Build the result map with all chart data
    const chartDataMap = new Map()

    for (const project of projects) {
      const compassId = project.id
      const projectFrameworks = frameworksByCompass[compassId] || []

      // Find default framework
      let defaultFramework = projectFrameworks.find(fw => fw.isDefault)
      const defaultFrameworkId = defaultFramework ? defaultFramework.frameworkId : 1

      // Merge with framework settings
      const settings = frameworkSettings.find(s => s.frameworkId === defaultFrameworkId)
      if (settings) {
        defaultFramework = { ...defaultFramework, ...settings }
      }

      // Get categories for this compass + framework
      const categoryKey = `${compassId}_${defaultFrameworkId}`
      const rawCategories = categoriesByCompassAndFramework[categoryKey] || []

      // Process categories (add icons)
      const categories = rawCategories
        .map(category => {
          let iconUrl = ''
          if (category.iconSvg) {
            const base64 = btoa(category.iconSvg)
            iconUrl = `data:image/svg+xml;base64,${base64}`
          } else if (category.icon) {
            iconUrl = `${store.state.urls.appWeb}/icons/${category.icon}.svg`
          }
          return {
            ...category,
            iconUrl,
          }
        })
        .sort((a, b) => a.position - b.position)

      chartDataMap.set(compassId, {
        framework: defaultFramework,
        frameworkId: defaultFrameworkId,
        categories,
      })
    }

    console.log(`Preloaded data for ${chartDataMap.size} projects`)
    return chartDataMap
  } catch (error) {
    console.error('Error preloading projects data:', error)
    return new Map()
  }
}

/**
 * Preloads dataset values for all projects in bulk
 * This is called after basic project data is loaded
 * @param {Array<Object>} projects - Array of project/compass objects
 * @param {Map} chartDataMap - Map of compassId -> basic chart data
 * @param {Object} store - Vuex store instance
 * @returns {Promise<Map>} Updated map with dataset values
 */
export const preloadProjectsDatasets = async (projects, chartDataMap, store) => {
  if (!projects || projects.length === 0) {
    return chartDataMap
  }

  console.log(`Preloading datasets for ${projects.length} projects...`)

  // Sequentially load datasets for each project to avoid overwhelming the server
  // We can parallelize this in chunks if needed
  const CHUNK_SIZE = 5
  for (let i = 0; i < projects.length; i += CHUNK_SIZE) {
    const chunk = projects.slice(i, i + CHUNK_SIZE)

    await Promise.all(chunk.map(async (project) => {
      const compassId = project.id
      const chartData = chartDataMap.get(compassId)

      if (!chartData) {
        return
      }

      try {
        // Use the existing store action to get datasets
        const datasets = await store.dispatch('getFrameworkDatasets', {
          compassId,
          frameworkId: chartData.frameworkId,
        })

        chartDataMap.set(compassId, {
          ...chartData,
          datasets,
        })
      } catch (error) {
        console.error(`Error loading datasets for compass ${compassId}:`, error)
        // Keep the existing data without datasets
      }
    }))
  }

  console.log(`Preloaded datasets for ${chartDataMap.size} projects`)
  return chartDataMap
}
